import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges, OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {CurrentUser} from '../../user/current-user.service';
import {TranslatorService} from '../../translation/translator.service';
import {ChatbotChat, ChatbotChatCreate} from '../state/chatbot-chat';
import {ChatbotChatService} from '../api-services/chatbot-chat.service';
import {ChatbotSendMessageService} from '../utility-services/chatbot-send-message.service';
import {ChatbotChatCreateService} from '../utility-services/chatbot-chat-create-service';
import {ChatbotChatUser} from '../state/chatbot-chat-user';
import {FormGroup} from '@angular/forms';
import {ChatbotChatCreateAdminFormService} from '../utility-services/chatbot-chat-create-admin-form.service';
import {ChatbotUserService} from '../api-services/chatbot-user.service';
import {firstValueFrom} from 'rxjs';
import {ChatbotMessagePairService} from '../api-services/chatbot-message-pair.service';
import {UserSearchService} from '../../user/user-search.service';
import {UserGroup} from '../../user/user-group';
import {ChatbotUserCreateService} from '../utility-services/chatbot-user-create.service';
import {environment} from '../../../environments/environment';
import {AutosizeDirective} from 'ngx-autosize';
import {BreakpointObserver} from '@angular/cdk/layout';
import {ChatbotProfileService} from '../api-services/chatbot-profile.service';
import {ChatbotAvailableProfile} from '../state/chatbot-available-profile';
import {ChatbotAvailableTeam} from '../state/chatbot-available-team';
import {ChatbotProfile} from '../state/chatbot-profile';
import {TeamService} from '../../team/state/team.service';
import {MessagePairProfile} from '../state/chatbot-message-pair';
import {ChatbotProfileProviderService} from '../services/chatbot-profile-provider.service';
import {TestVariation} from '../../test/state/test-variation';
import {FriendshipFunctions} from '../../friend/state/friendship-functions';
import {FriendshipQuery} from '../../friend/state/friendship.query';

@Component({
  selector: 'app-chatbot-chat-input',
  templateUrl: './chatbot-chat-input.component.html',
  styleUrls: ['./chatbot-chat-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [AutosizeDirective]
})
export class ChatbotChatInputComponent implements OnInit , OnChanges, OnDestroy{

  @Input()
    chatbotChat: ChatbotChat;

  @Output() sendingMessage = new EventEmitter<any>();

  @Output() chatCreated = new EventEmitter<ChatbotChat>();

  @Input() adminCreateFormOpen: boolean = false;

  @Input()
    errorOccurred: boolean;
  @Input()
    errorMessage: string;

  @ViewChild('messageInput') messageInput: ElementRef<HTMLTextAreaElement>;

  adminCreateForm: FormGroup;
  sendWithEnter: boolean = true;
  message: string = '';
  inputFocused: boolean = false;
  isSending: boolean = false;
  isMobile: boolean = false;

  // Mention-related properties
  showMentionOverlay: boolean = false;
  mentionPosition = { top: 0, left: 0 };
  mentionSearch: string = '';
  mentionResults: UserGroup[] = [];
  mentionActiveIndex: number = 0;
  mentionJustInserted: boolean = false;
  mentionDefaultProfiles: ChatbotAvailableProfile[] = [];
  mentionDefaultTeams: ChatbotAvailableTeam[] = [];

  availableProfiles: ChatbotAvailableProfile[] = [];
  numberOfAvailableProfiles: number;
  currentProfilePageNumber: number = 1;
  isLoadingAvailableProfiles: boolean = false;

  availableTeams: ChatbotAvailableTeam[] = [];
  numberOfAvailableTeams: number;
  currentTeamPageNumber: number = 1;
  isLoadingAvailableTeams: boolean = false;

  isMeshModeAvailable: boolean = environment.FEATURE_CHATBOT_TEAMS_FUNCTION;
  profiles: ChatbotProfile[] = [];

  showTeamSizeWarning: boolean = false;
  readonly MAX_TEAM_SIZE = 20;

  // Flag to track when we're explicitly switching between chats
  private previousChatId: number | null = null;

  private mentionStartIndex: number = -1;

  constructor(private translatorService: TranslatorService,
              private sendMessageService: ChatbotSendMessageService,
              private chatbotChatService: ChatbotChatService,
              private chatbotUserService: ChatbotUserService,
              private createChatService: ChatbotChatCreateService,
              private cdr: ChangeDetectorRef,
              private adminFormService: ChatbotChatCreateAdminFormService,
              private currentUser: CurrentUser,
              private chatbotMessagePairService: ChatbotMessagePairService,
              private userSearchService: UserSearchService,
              private chatbotUserCreateService: ChatbotUserCreateService,
              private chatbotProfileService: ChatbotProfileService,
              private teamService: TeamService,
              private breakpointObserver: BreakpointObserver,
              private chatbotProfileProviderService: ChatbotProfileProviderService,
              private friendshipQuery: FriendshipQuery) {
    // Initialize form early to prevent template errors
    this.adminCreateForm = this.adminFormService.getAdminForm();
  }

  @HostListener('window:keydown', ['$event'])
  keyEventOverrideDefaults(event: KeyboardEvent) {
    if (this.showMentionOverlay) {
      if (['ArrowUp', 'ArrowDown', 'Enter', 'Escape'].includes(event.key)) {
        event.preventDefault();
        event.stopPropagation();
      }
      return;
    }

    if (event.key === 'Enter'
      && this.inputFocused
      && this.sendWithEnter
      && !event.shiftKey) {
      // Prevent, that enter adds an additional newline before sending the new message.
      event.preventDefault();
    }
  }

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent) {
    if (this.showMentionOverlay) {
      this.handleMentionKeyEvent(event);
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // Don't send message if a mention was just inserted
    if (this.mentionJustInserted && event.key === 'Enter') {
      this.mentionJustInserted = false;
      return;
    }

    if (event.key === 'Enter'
      && this.inputFocused
      && this.sendWithEnter && !event.shiftKey
      && !this.isMessageEmpty()) {
      this.send();
    }
  }

  ngOnInit(): void {
    // Initialize form first to prevent template errors
    this.adminCreateForm = this.adminFormService.getAdminForm();

    // Always reset profile state when component initializes (handles navigation-without-reload)
    if (!this.chatbotChat) {
      this.profiles = [];
      this.chatbotProfileProviderService.updateProfiles([]);
      this.chatbotProfileProviderService.setCurrentChat(null);
    }

    this.breakpointObserver.observe(['(max-width: 600px)']).subscribe(result => {
      this.isMobile = result.matches;
    });
    this.loadPaginatedAvailableProfiles();
    this.loadPaginatedAvailableTeams();
    this.loadDefaultProfilesAndTeam();

    this.chatbotProfileProviderService.profiles$.subscribe(profiles => {
      if (profiles && profiles.length > 0) {
        // Only use profiles from the provider if we have a current chat with matching profiles
        if (this.chatbotChat?.chat_id) {
          if (this.chatbotChat.profiles && this.chatbotChat.profiles.length > 0) {
            // Keep profiles that either belong to this chat on the backend OR were just added
            // locally (manual additions aren't in chatbotChat.profiles yet). Profiles in neither
            // list are stale leftovers from another chat and are dropped.
            const currentChatProfiles = profiles.filter(profile =>
              this.chatbotChat.profiles?.some(chatProfile => chatProfile.user_id === profile.keycloakId)
              || this.profiles.some(existing => existing.keycloakId === profile.keycloakId)
            );

            if (currentChatProfiles.length > 0) {
              this.profiles = currentChatProfiles;
              this.cdr.detectChanges();
            }
          }
          else {
            // Current chat has no backend profiles, but might have manually added profiles
            // Use all profiles from provider for this chat
            this.profiles = profiles;
            this.cdr.detectChanges();
          }
        }
        else if (!this.chatbotChat) {
          // No current chat - this means we're in new chat mode
          // Only clear profiles if they're coming from another chat context
          // Allow profiles to be added for new chats
          if (profiles.length === 0) {
            this.profiles = [];
            this.cdr.detectChanges();
          }
          else {
            // Keep profiles for new chat
            this.profiles = profiles;
            this.cdr.detectChanges();
          }
        }
      }
      else if (profiles && profiles.length === 0) {
        // Explicitly empty profiles - only clear if we're not in the middle of adding profiles
        if (!this.chatbotChat || (this.chatbotChat && (!this.chatbotChat.profiles || this.chatbotChat.profiles.length === 0))) {
          this.profiles = [];
          this.cdr.detectChanges();
        }
      }
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.adminCreateForm) {
      this.adminCreateForm = this.adminFormService.getAdminForm();
    }

    if (changes.chatbotChat) {
      const previousChat = changes.chatbotChat.previousValue as ChatbotChat | undefined;
      const chatChanged = previousChat?.chat_id !== this.chatbotChat?.chat_id;

      // Clear profiles only when leaving a real existing chat (switching between chats,
      // or back to new-chat mode). Do NOT clear when an empty new chat just became a real
      // chat (previousChat has no chat_id) — that would discard manually-added profiles.
      if (chatChanged && previousChat?.chat_id) {
        this.profiles = [];
        this.chatbotProfileProviderService.updateProfiles([]);
        this.chatbotProfileProviderService.setCurrentChat(null);
      }

      if (!this.chatbotChat) {
        this.previousChatId = null;
        this.cdr.detectChanges();
        return;
      }

      this.previousChatId = this.chatbotChat?.chat_id || null;
      this.chatbotProfileProviderService.setCurrentChat(this.chatbotChat.chat_id);

      // Drop profiles the user has explicitly removed for this chat — the backend still returns
      // them as part of the chat, so we filter them out on every load.
      const hiddenIds = this.chatbotChat.chat_id !== undefined
        ? this.chatbotProfileProviderService.getHiddenProfileIds(this.chatbotChat.chat_id)
        : new Set<string>();
      const backendProfiles = (this.chatbotChat.profiles ?? []).filter(p => !hiddenIds.has(p.user_id));

      if (backendProfiles.length && this.chatbotChat.chat_id) {
        const keycloakIds = backendProfiles.map(p => p.user_id);
        const cachedProfiles = this.getCachedProfilesForKeycloakIds(keycloakIds);

        if (cachedProfiles.length > 0) {
          this.profiles = cachedProfiles;
          this.chatbotProfileProviderService.updateProfiles(this.profiles, this.chatbotChat.chat_id);
          this.cdr.detectChanges();
        }

        this.chatbotProfileService.getUsersWithTests(keycloakIds).subscribe({
          next: (chatbotUserRepresentations) => {
            const freshProfiles = ChatbotProfile.buildFromList(
              backendProfiles, chatbotUserRepresentations
            );
            if (cachedProfiles.length === 0 || this.isProfileDataBetter(freshProfiles, this.profiles)) {
              this.profiles = freshProfiles;
            }
            this.chatbotProfileProviderService.updateProfiles(this.profiles, this.chatbotChat.chat_id);
            this.cdr.detectChanges();
          },
          error: () => {
            if (cachedProfiles.length === 0) {
              this.profiles = backendProfiles.map(p =>
                new ChatbotProfile(
                  p.user_id, p.status,
                  p.acronym || p.user_id.slice(0, 6),
                  'Unknown', 'User',
                  TestVariation.ID37_14.motiveCount
                )
              );
              this.chatbotProfileProviderService.updateProfiles(this.profiles, this.chatbotChat.chat_id);
              this.cdr.detectChanges();
            }
          }
        });
      }
      else {
        // Chat has no backend profiles yet. Keep any manually-added profiles (e.g. a brand
        // new chat just created from an empty chat) and associate them with this chat.
        this.chatbotProfileProviderService.updateProfiles(this.profiles, this.chatbotChat.chat_id);
        this.cdr.detectChanges();
      }
    }
  }

  ngOnDestroy(): void {
    // Clear service state when navigating away so it doesn't bleed into the next session
    if (!this.chatbotChat) {
      this.chatbotProfileProviderService.updateProfiles([]);
      this.chatbotProfileProviderService.setCurrentChat(null);
    }
  }

  onInput(): void {
    const cursorPosition: number = this.messageInput.nativeElement.selectionStart;
    const textBeforeCursor = this.message.substring(0, cursorPosition);

    const lastAtSymbol = textBeforeCursor.lastIndexOf('@');

    // Check if we should show mention overlay
    if (lastAtSymbol !== -1) {
      const textAfterAt = textBeforeCursor.substring(lastAtSymbol + 1);

      // Show overlay if we have @ followed by no spaces
      // Remove the restriction that cursor must be at the end - allow mention search anywhere
      if (!textAfterAt.includes(' ')) {
        this.mentionStartIndex = lastAtSymbol;
        this.mentionSearch = textAfterAt;
        this.showMentionOverlay = true;
        this.mentionActiveIndex = 0;
        this.calculateMentionPosition();
        this.searchMentions(textAfterAt);
      }
      else {
        this.closeMentionOverlay();
      }
    }
    else {
      this.closeMentionOverlay();
    }
  }

  OnAtButtonClick(): void {
    this.closeMentionOverlay();
  }

  loadPaginatedAvailableProfiles(pageNumber: number = 1, pageSize: number = 5): void {
    this.isLoadingAvailableProfiles = true;
    this.chatbotProfileService.loadProfilesPaginated(pageNumber, pageSize).subscribe({
      next: response => {
        this.availableProfiles = this.availableProfiles.concat(response.availableProfiles);
        this.numberOfAvailableProfiles = response.numberOfAvailableProfiles;
        this.isLoadingAvailableProfiles = false;
        this.cdr.detectChanges();
      },
      error: error => {
        console.log('Failed to load available profiles', error);
        this.isLoadingAvailableProfiles = false;
        this.cdr.detectChanges();
      }
    });
  }

  loadMoreProfiles(): void {
    this.currentProfilePageNumber++;
    this.loadPaginatedAvailableProfiles(this.currentProfilePageNumber);
  }

  loadPaginatedAvailableTeams(pageNumber: number = 1, pageSize: number = 5): void {
    this.isLoadingAvailableTeams = true;
    this.chatbotProfileService.loadTeamsPaginated(pageNumber, pageSize).subscribe({
      next: response => {
        this.availableTeams = this.availableTeams.concat(response.availableTeams);
        this.numberOfAvailableTeams = response.numberOfAvailableTeams;
        this.isLoadingAvailableTeams = false;
        this.cdr.detectChanges();
      },
      error: error => {
        console.log('Failed to load available teams', error);
        this.isLoadingAvailableTeams = false;
        this.cdr.detectChanges();
      }
    });
  }

  loadMoreTeams(): void {
    this.currentTeamPageNumber++;
    this.loadPaginatedAvailableTeams(this.currentTeamPageNumber);
  }

  loadDefaultProfilesAndTeam(): void {
    this.chatbotProfileService.loadProfilesPaginated(1, 5).subscribe({
      next: response => {
        this.mentionDefaultProfiles = response.availableProfiles;
        this.cdr.detectChanges();
      }
    });
    this.chatbotProfileService.loadTeamsPaginated(1, 2).subscribe({
      next: response => {
        this.mentionDefaultTeams = response.availableTeams;
        this.cdr.detectChanges();
      }
    });
  }

  activeProfiles(): ChatbotProfile[] {
    return this.profiles.filter(profile => profile.status !== 'DELETED');
  }

  addProfileToMessage(profile: ChatbotProfile): void {
    this.message += '@' + profile.firstName + ' ';
  }

  async insertMention(user: UserGroup): Promise<void> {
    // Store the mention data before closing overlay (since closeMentionOverlay resets mentionStartIndex to -1)
    const mentionStartPos = this.mentionStartIndex;
    const mentionSearchText = this.mentionSearch;

    // Close overlay immediately to prevent any interference
    this.closeMentionOverlay();

    // Set flag to prevent immediate message sending
    this.mentionJustInserted = true;

    // Find the end of the current mention search term based on actual search text length
    const mentionSearchEndPos = mentionStartPos + 1 + mentionSearchText.length; // +1 for the '@' symbol

    // Calculate positions for text replacement
    const beforeMention = this.message.substring(0, mentionStartPos);
    const afterMentionSearch = this.message.substring(mentionSearchEndPos);
    const mentionText = '@' + user.firstName + ' ';

    // Replace only the mention search part (@ + search text), preserve everything else
    this.message = beforeMention + mentionText + afterMentionSearch;

    // Force change detection to update the textarea
    this.cdr.detectChanges();

    // Set cursor position after the inserted mention
    setTimeout(() => {
      const newCursorPosition = beforeMention.length + mentionText.length;
      this.messageInput.nativeElement.setSelectionRange(newCursorPosition, newCursorPosition);
      this.messageInput.nativeElement.focus();

      // Clear the flag after a short delay to allow normal Enter behavior afterwards
      setTimeout(() => {
        this.mentionJustInserted = false;
      }, 100);
    }, 10);

    try {
      await this.addNewProfile(
        new ChatbotAvailableProfile(
          user.keycloakId,
          user.firstName,
          user.lastName
        )
      );
    }
    catch (error) {
      console.error('Error adding user to team:', error);
    }
  }

  closeMentionOverlay(): void {
    this.showMentionOverlay = false;
    this.mentionResults = [];
    this.mentionStartIndex = -1;
  }

  send(): void {
    // case 1: already existing chatbot-chat
    if (this.chatbotChat) {
      this.sendMessage().catch(error => console.error('Error send Message enter key:', error));
    }
    // case 2: new chat as administrator
    else if (this.currentUser.hasRole('administrator')) {
      this.openForm();
    }
    // case 3: new chat as regular user
    else {
      this.createNewChatAndSendMessage();
    }
  }

  async sendMessage(): Promise<void> {
    if (this.isMessageEmpty() || this.isChatbotActive()) {
      return;
    }
    this.showTeamSizeWarning = false;
    this.sendingMessage.emit();
    // Store message to send and clear input field.
    const messageToSend = this.message;
    const processedMessage = this.chatbotProfileProviderService.replaceNamesWithKeycloakIds(messageToSend);

    this.message = '';
    if (this.profiles.length === 0){
      await this.sendMessageService.sendMessage(processedMessage, this.chatbotChat)
        .catch(error => console.error('Error occurred:', error))
        .finally(() => {
          this.isSending = false;
          this.sendingMessage.emit();
        });
    }
    else {
      const profilesToSend: MessagePairProfile[] = this.profiles.map(profile =>
        new MessagePairProfile(profile.keycloakId, profile.status, this.chatbotChat.chat_id, profile.acronym, profile.variation)
      );
      await this.sendMessageService.sendMessageTeam(processedMessage, this.chatbotChat, profilesToSend)
        .catch(error => console.error('Error occurred:', error))
        .finally(() => {
          this.isSending = false;
          this.sendingMessage.emit();
        });
    }
  }


  isMessageEmpty(): boolean {
    return (!this.message || this.message === '');
  }

  isChatbotActive(): boolean {
    return this.chatbotChat && this.chatbotChat.sending;
  }

  openForm(): void {
    this.adminCreateFormOpen = true;
  }

  createNewChatAndSendMessage() {
    this.createChat().then(value => {
      this.chatbotChat = value;
      this.sendMessage().catch(error => console.error('Failed to send message', error));
    }).catch(error => console.error('Failed to create chat', error));
  }

  async newChatAdmin() {
    await this.createChatAdmin();
    await this.sendMessage();
  }

  newChatSample(content: string) {
    this.message = content;

    // Don't clear profiles when using suggestion prompts - keep any previously added profiles
    // This allows users to add profiles and then use suggestions

    if (this.currentUser.hasRole('administrator')) {
      this.openForm();
      this.cdr.detectChanges();
    }
    else {
      try {
        this.createNewChatAndSendMessage();
      }
      catch (error) {
        console.error('Error new sample Chat ', error);
      }
    }
  }

  async createChat(): Promise<ChatbotChat> {
    let newChat: ChatbotChat;

    await this.createChatService.createChat().then(chat => {
      // this.chatbotUserChanged.emit(values[0]);
      this.chatCreated.emit(chat);
      newChat = chat;
    });
    return newChat;
  }

  async createChatAdmin() {
    const motiveKeys = ['NEU', 'SAN', 'EIN', 'STA', 'BES', 'AUT', 'SOZ', 'PRI', 'SEN', 'STR', 'SIC', 'REV', 'BEW', 'ESS', 'FAM', 'SIN'];
    const motives = motiveKeys.map(key => {
      const control = this.adminCreateForm.get(key);
      return control ? Number(control.value) : 0;
    });

    const chatbotchatuser = new ChatbotChatUser(
      this.currentUser.keycloakId,
      motives[0], motives[1], motives[2], motives[3], motives[4], motives[5], motives[6], motives[7], motives[8],
      motives[9], motives[10], motives[11], motives[12], motives[13], motives[14], motives[15]
    );

    // Check if user exists, create if not, then update motives
    try {
      const userExists = await firstValueFrom(this.chatbotUserService.checkChatbotUserExists());

      if (!userExists) {
        // Create user first
        await firstValueFrom(this.chatbotUserService.createChatbotUser(chatbotchatuser));
      }
      else {
        // Update existing user motives
        await firstValueFrom(this.chatbotUserService.updateChatbotUser(chatbotchatuser));
      }
    }
    catch (error) {
      // If check fails, try to create user (fallback)
      try {
        await firstValueFrom(this.chatbotUserService.createChatbotUser(chatbotchatuser));
      }
      catch (createError) {
        // If create also fails, try update (user might exist but check failed)
        await firstValueFrom(this.chatbotUserService.updateChatbotUser(chatbotchatuser));
      }
    }

    // Create the chat
    const chatCreate = new ChatbotChatCreate(this.currentUser.keycloakId);
    const addChat = this.chatbotChatService.addChat(chatCreate);
    const chat = await firstValueFrom(addChat);

    this.chatbotChat = chat;
    this.chatCreated.emit(chat);

    // Close the admin form
    this.adminCreateFormOpen = false;
  }

  stopChatbot(): void {
    const chatbotId: number = this.chatbotChat.chat_id;
    this.chatbotMessagePairService.stopStreaming(chatbotId).subscribe();
  }

  async addNewProfile(newProfile: ChatbotAvailableProfile) {
    const activeProfileExists = this.profiles.some(profile =>
      profile.keycloakId === newProfile.keycloakId && profile.status !== 'DELETED'
    );

    let newProfileVariation: number;
    try {
      const friendship = this.friendshipQuery.getFriendshipWith(newProfile.keycloakId);

      // Check friendship first, THEN access its properties
      if (friendship && friendship.testShares && friendship.testShares.length > 0) {
        newProfileVariation = FriendshipFunctions
          .getFriendTestShare(friendship, this.currentUser.keycloakId)
          .variation
          .motiveCount;
      }
      else {
        newProfileVariation = TestVariation.ID37_16.motiveCount;
      }
    }
    catch (error) {
      newProfileVariation = TestVariation.ID37_16.motiveCount;
    }


    if (activeProfileExists) {
      return;
    }

    if (this.isProfileDeleted(newProfile.keycloakId)) {
      // Reaktiviere gelöschtes Profil
      this.reactivateProfile(newProfile.keycloakId);
    }
    else {
      // Erstelle neues Profil
      await this.chatbotUserCreateService.createUserForTeams(newProfile);
      this.createNewProfile(newProfile, newProfileVariation);
    }

    const chatId = this.chatbotChat?.chat_id;
    if (chatId !== undefined) {
      // Re-adding a previously removed profile cancels its persisted removal.
      this.chatbotProfileProviderService.unhideProfileForChat(chatId, newProfile.keycloakId);
    }
    this.chatbotProfileProviderService.updateProfiles(this.profiles, chatId);

    const activeProfilesCount = this.profiles.filter(p => p.status !== 'DELETED').length;
    if (activeProfilesCount > this.MAX_TEAM_SIZE && !this.showTeamSizeWarning) {
      this.showLargeTeamWarning();
    }

    // Update profile provider service
    this.chatbotProfileProviderService.updateProfiles(this.profiles);
    this.cdr.detectChanges();
  }

  private findProfileByKeycloakId(keycloakId: string): ChatbotProfile | undefined {
    return this.profiles.find(profile => profile.keycloakId === keycloakId);
  }

  private isProfileDeleted(keycloakId: string): boolean {
    const profile = this.findProfileByKeycloakId(keycloakId);
    return profile?.status === 'DELETED';
  }

  private reactivateProfile(keycloakId: string): void {
    const profile = this.findProfileByKeycloakId(keycloakId);
    if (profile && profile.status === 'DELETED') {
      profile.status = 'ACTIVE';
    }
  }

  private createNewProfile(newProfile: ChatbotAvailableProfile, variation: number): void {
    this.profiles.push(ChatbotProfile.buildFrom(newProfile, variation));
  }

  addNewTeam(newTeam: ChatbotAvailableTeam) {
    this.closeMentionOverlay();
    this.teamService.loadTeamDetails(newTeam.teamId).subscribe(
      team => {
        if (team.users.length > this.MAX_TEAM_SIZE) {
          this.showLargeTeamWarning();
        }
        team.users.forEach(async user => {
          await this.addNewProfile(new ChatbotAvailableProfile(
            user.keycloakId,
            user.firstName,
            user.lastName
          ));
        });
      }
    );
  }

  removeProfiles(): void{
    const chatId = this.chatbotChat?.chat_id;
    const removedKeycloakIds = this.profiles.map(profile => profile.keycloakId);

    // Actually drop the profiles so the next message is sent without a team and the removal sticks.
    this.profiles = [];

    if (chatId !== undefined) {
      // Persist the removal so it survives reloads and chat switches, even though the backend
      // keeps returning these profiles as part of the chat.
      this.chatbotProfileProviderService.hideProfilesForChat(chatId, removedKeycloakIds);
      // updateProfiles() never clears the per-chat cache on an empty list, so do it explicitly,
      // otherwise revisiting the chat would restore the removed profiles from the stale cache.
      this.chatbotProfileProviderService.clearCache(chatId);
    }
    this.chatbotProfileProviderService.updateProfiles([], chatId);
    this.cdr.detectChanges();
  }

  private showLargeTeamWarning(): void {
    this.showTeamSizeWarning = true;
    this.cdr.detectChanges();
  }

  hasActiveProfiles(): boolean{
    return this.profiles.some(profile => profile.status !== 'DELETED');
  }

  get adminForm(): FormGroup {
    if (!this.adminCreateForm) {
      this.adminCreateForm = this.adminFormService.getAdminForm();
    }
    return this.adminCreateForm;
  }

  __(key: string): string {
    return this.translatorService.translate('chat.' + key);
  }

  private searchMentions(searchText: string): void {
    // Backend requires minimum 3 characters for search filter
    if (searchText.length >= 3) {
      this.userSearchService.searchUsersInFriendsAndGroups(searchText)
        .subscribe({
          next: (users: UserGroup[]) => {
            this.mentionResults = users;
            this.showMentionOverlay = users.length > 0;
            this.mentionActiveIndex = 0;
            this.cdr.markForCheck();
          },
          error: () => {
            this.closeMentionOverlay();
          }
        });
    }
    else {
      // Show overlay with empty results when less than 3 characters
      // This keeps the overlay visible but empty until user types enough characters
      this.mentionResults = [];
      this.showMentionOverlay = true;
      this.mentionActiveIndex = 0;
      this.cdr.markForCheck();
    }
  }

  private calculateMentionPosition(): void {
    const textarea = this.messageInput.nativeElement;

    // Create a mirror div to measure the @ symbol position
    const mirrorDiv = document.createElement('div');
    const computedStyle = window.getComputedStyle(textarea);

    // Copy all relevant styles from textarea to mirror div
    mirrorDiv.style.position = 'absolute';
    mirrorDiv.style.top = '-9999px';
    mirrorDiv.style.left = '-9999px';
    mirrorDiv.style.visibility = 'hidden';
    mirrorDiv.style.whiteSpace = 'pre-wrap';
    mirrorDiv.style.wordWrap = 'break-word';
    mirrorDiv.style.font = computedStyle.font;
    mirrorDiv.style.fontSize = computedStyle.fontSize;
    mirrorDiv.style.fontFamily = computedStyle.fontFamily;
    mirrorDiv.style.fontWeight = computedStyle.fontWeight;
    mirrorDiv.style.lineHeight = computedStyle.lineHeight;
    mirrorDiv.style.padding = computedStyle.padding;
    mirrorDiv.style.border = computedStyle.border;
    mirrorDiv.style.width = computedStyle.width;
    mirrorDiv.style.height = 'auto';
    mirrorDiv.style.overflow = 'hidden';
    mirrorDiv.style.boxSizing = computedStyle.boxSizing;

    document.body.appendChild(mirrorDiv);

    try {
      // Text up to and including the @ symbol
      mirrorDiv.textContent = this.message.substring(0, this.mentionStartIndex + 1);

      // Create a span to mark the end position (after @)
      const measureSpan = document.createElement('span');
      measureSpan.textContent = '';
      mirrorDiv.appendChild(measureSpan);

      // Get the position of the @ symbol
      const spanRect = measureSpan.getBoundingClientRect();
      const mirrorRect = mirrorDiv.getBoundingClientRect();

      // Calculate position relative to the textarea
      const relativeLeft = spanRect.left - mirrorRect.left;
      const relativeTop = spanRect.top - mirrorRect.top;

      // Position overlay with bottom-left corner anchored at @ symbol
      const textareaPaddingLeft = parseInt(computedStyle.paddingLeft, 10) || 0;
      const textareaPaddingTop = parseInt(computedStyle.paddingTop, 10) || 0;

      this.mentionPosition = {
        top: relativeTop + textareaPaddingTop,
        left: relativeLeft + textareaPaddingLeft
      };

    }
    catch (error) {
      console.error('Error calculating mention position:', error);
      // Fallback to simple positioning at textarea top
      this.mentionPosition = {
        top: 20,
        left: 0
      };
    }
    finally {
      document.body.removeChild(mirrorDiv);
    }
  }

  updateActiveIndex(i: number){
    this.mentionActiveIndex = i;
  }

  private handleMentionKeyEvent(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        this.mentionActiveIndex = Math.min(this.mentionActiveIndex + 1, this.mentionResults.length - 1);
        break;
      case 'ArrowUp':
        this.mentionActiveIndex = Math.max(this.mentionActiveIndex - 1, 0);
        break;
      case 'Enter':
        if (this.mentionResults[this.mentionActiveIndex]) {
          void this.insertMention(this.mentionResults[this.mentionActiveIndex]);
        }
        break;
      case 'Escape':
        this.closeMentionOverlay();
        break;
    }
  }

  private getCachedProfilesForKeycloakIds(keycloakIds: string[]): ChatbotProfile[] {
    const cachedProfiles: ChatbotProfile[] = [];

    for (const keycloakId of keycloakIds) {
      const cachedProfile = this.chatbotProfileProviderService.getProfileFromGlobalCache(keycloakId);
      if (cachedProfile) {
        cachedProfiles.push(cachedProfile);
      }
    }

    return cachedProfiles;
  }

  private isProfileDataBetter(freshProfiles: ChatbotProfile[], currentProfiles: ChatbotProfile[]): boolean {
    if (freshProfiles.length > currentProfiles.length) {
      return true;
    }

    return currentProfiles.some(profile =>
      profile.firstName === 'Unknown' || profile.lastName === 'User'
    );


  }
}
